import { z } from "zod";

import type {
  AlertRule,
  FleetColumn,
  FleetNode,
  FleetView,
  MaintenanceScope,
  MaintenanceWindow,
  Project,
  ProjectDraft,
} from "./domain";
import { createAlertCondition } from "./domain";

const fleetNodeSchema = z.object({
  id: z.string(),
  parentNodeId: z.string().uuid().nullable().default(null),
  name: z.string(),
  architecture: z.string(),
  release: z.string(),
  lifecycle: z.string(),
  lastSeenAt: z.string(),
  lastSequence: z.number().int(),
  localGuestCount: z.number().int().nonnegative().nullable().default(null),
  lifecycleMode: z.enum(["create", "adopt"]).nullable().default(null),
  duplicateOf: z.array(z.string()).default([]),
  health: z.enum(["healthy", "warning", "critical"]),
  diskUsedPercent: z.number(),
  memoryUsedPercent: z.number().nullable(),
  backup: z.discriminatedUnion("state", [
    z.object({ state: z.literal("never-verified") }),
    z.object({ state: z.literal("verified"), verifiedAt: z.string() }),
    z.object({ state: z.literal("failed"), attemptedAt: z.string() }),
  ]),
  update: z.discriminatedUnion("state", [
    z.object({ state: z.literal("current"), latestRelease: z.string() }),
    z.object({ state: z.literal("available"), latestRelease: z.string() }),
    z.object({
      state: z.literal("blocked"),
      latestRelease: z.string(),
      reason: z.string(),
    }),
  ]),
  tags: z.array(z.string()).default([]),
  projectIds: z.array(z.string().uuid()).default([]),
  projectNames: z.array(z.string()).default([]),
  locations: z.array(z.string()).default([]),
  capabilities: z.array(z.string()).default([]),
});

const projectSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  customerName: z.string(),
  contactName: z.string(),
  email: z.string(),
  phone: z.string(),
  location: z.string(),
  address: z.string(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  notes: z.string(),
  nodeIds: z.array(z.string().uuid()),
  createdAt: z.string(),
  updatedAt: z.string(),
  mapPolicy: z.enum(["exact", "approximate", "hidden"]).default("hidden"),
});

/** Authenticated dashboard state returned after the application second factor. */
export interface AdminState {
  readonly nodes: readonly FleetNode[];
  readonly csrf: string;
  readonly pagination?: {
    readonly page: number;
    readonly limit: number;
    readonly total: number;
    readonly pages: number;
  };
}

/** Closed, URL-safe filter grammar accepted by the paged inventory endpoint. */
export interface NodePageRequest {
  readonly page: number;
  readonly limit: number;
  readonly query: string;
  readonly statuses: readonly string[];
  readonly tags: readonly string[];
  readonly projectIds: readonly string[];
  readonly locations: readonly string[];
  readonly releases: readonly string[];
  readonly capabilities: readonly string[];
  readonly maxContactAgeMinutes: number | null;
  readonly sort: "name" | "status" | "last-seen";
}

/** Bounded operational history for a selected node. */
export interface NodeDetail {
  readonly localGuests: readonly {
    readonly id: number;
    readonly kind: "vm" | "lxc";
    readonly role: "haos" | "docker-lxc";
    readonly status: "running" | "stopped" | "unknown";
    readonly origin?: "installer-created" | "operator-created" | undefined;
  }[] | null;
  /** Adoption provenance; null for hosts whose agent predates v1.9.0. */
  readonly provenance: {
    readonly lifecycleMode: "create" | "adopt";
    readonly deploymentTarget: "proxmox-appliance" | "native-docker";
    readonly resources: readonly {
      readonly kind: string;
      readonly identifier: string;
      readonly origin: "installer-created" | "operator-created" | "external";
      readonly scope: "managed" | "observed" | "ignored";
    }[];
  } | null;
  readonly observations: readonly {
    readonly sequence: number;
    readonly observed_at: string;
    readonly health: string;
    readonly disk_used_percent: number;
    readonly memory_used_percent: number | null;
  }[];
  readonly events: readonly {
    readonly occurred_at: string;
    readonly severity: string;
    readonly message: string;
  }[];
  readonly services: readonly {
    readonly catalog_id: string;
    readonly display_name: string;
    readonly state: "running" | "stopped" | "restarting" | "unknown";
    readonly health: "healthy" | "unhealthy" | "starting" | "none";
    readonly restart_count: number;
    readonly image: string;
    readonly image_digest: string | null;
    readonly dependencies: readonly string[];
    readonly observed_at: string;
  }[];
  readonly software: {
    readonly target: "host" | "lxc-101" | "vm-102";
    readonly agent_version: string | null;
    readonly appliance_version: string | null;
    readonly os_version: string | null;
    readonly docker_version: string | null;
    readonly compose_version: string | null;
    readonly immutable: number;
    readonly observed_at: string;
  } | null;
}

/** Operations configuration and evidence returned by the administration API. */
export interface OperationsState {
  readonly views: readonly FleetView[];
  readonly maintenance: readonly MaintenanceWindow[];
  readonly rules: readonly AlertRule[];
  readonly incidents: readonly {
    readonly id: string;
    readonly title: string;
    readonly severity: string;
    readonly state: string;
    readonly openedAt: string;
  }[];
  readonly incidentEvents: readonly {
    readonly id: string;
    readonly incidentId: string;
    readonly occurredAt: string;
    readonly actor: string;
    readonly kind: string;
    readonly message: string;
    readonly nodeId?: string | null;
    readonly serviceId?: string | null;
  }[];
  readonly policies: readonly {
    readonly component: string;
    readonly desiredVersion: string;
    readonly minimumSupportedVersion: string | null;
    readonly immutableRequired: boolean;
  }[];
  readonly servicePolicies: readonly {
    readonly catalogId: string;
    readonly desiredImage: string;
    readonly desiredDigest: string;
  }[];
  readonly reportJobs: readonly {
    readonly id: string;
    readonly format: "pdf" | "csv";
    readonly scope: { readonly type: "fleet" } | {
      readonly type: "project";
      readonly projectId: string;
    };
    readonly window: "24h" | "7d" | "30d";
    readonly state: "queued" | "ready" | "failed" | "expired";
    readonly requestedAt: string;
    readonly expiresAt: string;
    readonly errorCode: string | null;
    readonly sizeBytes: number | null;
    readonly completedAt: string | null;
  }[];
  readonly backups: readonly {
    readonly id: string;
    readonly target: string;
    readonly capturedAt: string;
    readonly state: "succeeded" | "failed";
    readonly restoreTestedAt: string | null;
    readonly restoreSchemaVerified: boolean;
    readonly restoreDecryptionVerified: boolean;
    readonly keyEscrowVerified: boolean;
    readonly errorCode: string | null;
  }[];
  readonly software: readonly {
    readonly nodeId: string;
    readonly nodeName: string;
    readonly projectIds: readonly string[];
    readonly projectNames: readonly string[];
    readonly target: string;
    readonly agent: string | null;
    readonly appliance: string | null;
    readonly os: string | null;
    readonly docker: string | null;
    readonly compose: string | null;
    readonly immutable: boolean;
    readonly observedAt: string;
  }[];
  readonly serviceInventory: readonly {
    readonly nodeId: string;
    readonly nodeName: string;
    readonly catalogId: string;
    readonly image: string;
    readonly imageDigest: string | null;
    readonly state: string;
    readonly health: string;
    readonly observedAt: string;
  }[];
}

/** Fetch and validate the node inventory and mutation nonce at the browser boundary. */
export async function fetchNodes(): Promise<AdminState> {
  const schema = z.object({
    nodes: z.array(fleetNodeSchema),
    csrf: z.string().uuid(),
    pagination: z.object({
      page: z.number().int().positive(),
      limit: z.number().int().positive(),
      total: z.number().int().nonnegative(),
      pages: z.number().int().nonnegative(),
    }).optional(),
  });
  const readPage = async (page: number): Promise<z.infer<typeof schema>> => {
    const response = await fetch(`/api/admin/nodes?page=${page}&limit=100`, {
      credentials: "same-origin",
    });
    if (!response.ok) {
      throw new Error(
        response.status === 401
          ? "second-factor-required"
          : "inventory-unavailable",
      );
    }
    return schema.parse(await response.json());
  };
  const first = await readPage(1);
  const pages = first.pagination?.pages ?? 1;
  // A personal fleet should never approach this guard; it prevents an inconsistent response from causing unbounded browser requests.
  if (pages > 100) throw new Error("inventory-page-limit");
  const remaining = await Promise.all(
    Array.from(
      { length: Math.max(0, pages - 1) },
      (_, index) => readPage(index + 2),
    ),
  );
  const nodes = [first, ...remaining].flatMap((page) => page.nodes);
  return first.pagination
    ? { nodes, csrf: first.csrf, pagination: { ...first.pagination, page: 1 } }
    : { nodes, csrf: first.csrf };
}

/** Fetch one server-filtered inventory page for the dashboard list. */
export async function fetchNodePage(
  request: NodePageRequest,
): Promise<AdminState> {
  const parameters = new URLSearchParams({
    page: String(request.page),
    limit: String(request.limit),
  });
  if (request.query) parameters.set("query", request.query);
  if (request.statuses.length) {
    parameters.set("status", request.statuses.join(","));
  }
  request.tags.forEach((value) => parameters.append("tag", value));
  request.projectIds.forEach((value) => parameters.append("project", value));
  request.locations.forEach((value) => parameters.append("location", value));
  request.releases.forEach((value) => parameters.append("release", value));
  request.capabilities.forEach((value) =>
    parameters.append("capability", value)
  );
  if (request.maxContactAgeMinutes !== null) {
    parameters.set("max_age", String(request.maxContactAgeMinutes));
  }
  parameters.set("sort", request.sort);
  const response = await fetch(`/api/admin/nodes?${parameters.toString()}`, {
    credentials: "same-origin",
  });
  if (!response.ok) throw new Error("inventory-page-unavailable");
  return z.object({
    nodes: z.array(fleetNodeSchema),
    csrf: z.string().uuid(),
    pagination: z.object({
      page: z.number().int().positive(),
      limit: z.number().int().positive(),
      total: z.number().int().nonnegative(),
      pages: z.number().int().nonnegative(),
    }),
  }).parse(await response.json());
}

/** Exchange a TOTP code for a short-lived second-factor session. */
export async function verifySecondFactor(code: string): Promise<void> {
  const response = await fetch("/api/admin/session", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code }),
  });
  if (!response.ok) throw new Error("verification-failed");
}

/** Enumerate operator-safe approval outcomes without exposing response bodies. */
export type EnrollmentApprovalFailure = "unavailable" | "parent" | "session" | "rate-limited" | "service";

/** Carry a validated, non-sensitive approval failure to the UI. */
export class EnrollmentApprovalError extends Error {
  /** Create an operator-safe approval error. */
  constructor(readonly reason: EnrollmentApprovalFailure) {
    super(reason);
  }
}

const approvalErrorSchema = z.object({ error: z.string() });

const pendingEnrollmentSchema = z.object({
  userCode: z.string(),
  displayName: z.string(),
  architecture: z.string(),
  createdAt: z.string(),
  expiresAt: z.string(),
  ageSeconds: z.number().int().nonnegative(),
  publicKeyFingerprint: z.string().regex(/^[a-f0-9]{64}$/u),
});

/** What an operator reviews before approving: the node's claimed identity and its key fingerprint. */
export type PendingEnrollment = z.infer<typeof pendingEnrollmentSchema>;

/** Look up a pending enrollment for review without approving or consuming its code. */
export async function lookupEnrollment(userCode: string): Promise<PendingEnrollment> {
  const response = await fetch(
    `/api/admin/enrollments/${encodeURIComponent(userCode.toUpperCase())}`,
    { credentials: "same-origin" },
  );
  if (response.status === 404) throw new EnrollmentApprovalError("unavailable");
  if (response.status === 401 || response.status === 403) {
    throw new EnrollmentApprovalError("session");
  }
  if (response.status === 429) throw new EnrollmentApprovalError("rate-limited");
  if (!response.ok) throw new EnrollmentApprovalError("service");
  const parsed = z.object({ enrollment: pendingEnrollmentSchema }).safeParse(
    await response.json().catch(() => null),
  );
  if (!parsed.success) throw new EnrollmentApprovalError("service");
  return parsed.data.enrollment;
}

/** Approve one short-lived node enrollment using origin and CSRF binding. */
export async function approveEnrollment(
  userCode: string,
  csrf: string,
  parentNodeId: string | null = null,
): Promise<void> {
  const response = await fetch(
    `/api/admin/enrollments/${
      encodeURIComponent(userCode.toUpperCase())
    }/approve`,
    {
      method: "POST",
      credentials: "same-origin",
      headers: { "X-CSRF-Token": csrf, "Content-Type": "application/json" },
      body: JSON.stringify({ parentNodeId }),
    },
  );
  if (!response.ok) {
    const payload: unknown = await response.json().catch(() => null);
    const parsed = approvalErrorSchema.safeParse(payload);
    if (response.status === 409 && parsed.success && parsed.data.error === "parent-unavailable") {
      throw new EnrollmentApprovalError("parent");
    }
    if (response.status === 409 && parsed.success && parsed.data.error === "enrollment-unavailable") {
      throw new EnrollmentApprovalError("unavailable");
    }
    if (response.status === 401 || response.status === 403) {
      throw new EnrollmentApprovalError("session");
    }
    if (response.status === 429) {
      throw new EnrollmentApprovalError("rate-limited");
    }
    throw new EnrollmentApprovalError("service");
  }
}

/** Fetch bounded observations and meaningful events for one enrolled node. */
export async function fetchNodeDetail(nodeId: string): Promise<NodeDetail> {
  const response = await fetch(
    `/api/admin/nodes/${encodeURIComponent(nodeId)}`,
    { credentials: "same-origin" },
  );
  if (!response.ok) throw new Error("detail-unavailable");
  return z.object({
    localGuests: z.array(z.object({
      id: z.number().int().min(100),
      kind: z.enum(["vm", "lxc"]),
      role: z.enum(["haos", "docker-lxc"]),
      status: z.enum(["running", "stopped", "unknown"]),
      origin: z.enum(["installer-created", "operator-created"]).optional(),
    }).strict()).nullable(),
    provenance: z.object({
      lifecycleMode: z.enum(["create", "adopt"]),
      deploymentTarget: z.enum(["proxmox-appliance", "native-docker"]),
      resources: z.array(z.object({
        kind: z.string().max(32),
        identifier: z.string().max(64),
        origin: z.enum(["installer-created", "operator-created", "external"]),
        scope: z.enum(["managed", "observed", "ignored"]),
      }).strict()).max(32),
    }).strict().nullable().default(null),
    observations: z.array(
      z.object({
        sequence: z.number().int(),
        observed_at: z.string(),
        health: z.string(),
        disk_used_percent: z.number(),
        memory_used_percent: z.number().nullable(),
      }),
    ),
    events: z.array(
      z.object({
        occurred_at: z.string(),
        severity: z.string(),
        message: z.string(),
      }),
    ),
    services: z.array(
      z.object({
        catalog_id: z.string().regex(/^[a-z0-9][a-z0-9._-]{0,63}$/u),
        display_name: z.string().trim().min(1).max(80),
        state: z.enum(["running", "stopped", "restarting", "unknown"]),
        health: z.enum(["healthy", "unhealthy", "starting", "none"]),
        restart_count: z.number().int().nonnegative(),
        image: z.string().min(1).max(300),
        image_digest: z.string().regex(/^sha256:[a-f0-9]{64}$/u).nullable(),
        dependencies_json: z.string().max(4096),
        observed_at: z.string().datetime(),
      }).transform(({ dependencies_json, ...service }) => {
        try {
          const parsed: unknown = JSON.parse(dependencies_json);
          const dependencies = z.array(
            z.string().regex(/^[a-z0-9][a-z0-9._-]{0,63}$/u),
          ).max(32).safeParse(
            parsed,
          );
          return {
            ...service,
            dependencies: dependencies.success ? dependencies.data : [],
          };
        } catch {
          return { ...service, dependencies: [] };
        }
      }),
    ),
    software: z.object({
      target: z.enum(["host", "lxc-101", "vm-102"]),
      agent_version: z.string().nullable(),
      appliance_version: z.string().nullable(),
      os_version: z.string().nullable(),
      docker_version: z.string().nullable(),
      compose_version: z.string().nullable(),
      immutable: z.number(),
      observed_at: z.string(),
    }).nullable(),
  }).parse(await response.json());
}

/** Replace operator-managed node tags without altering node-reported capabilities. */
export async function saveNodeTags(
  nodeId: string,
  tags: readonly string[],
  csrf: string,
): Promise<void> {
  const response = await fetch(
    `/api/admin/nodes/${encodeURIComponent(nodeId)}/tags`,
    {
      method: "PUT",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrf },
      body: JSON.stringify({ tags }),
    },
  );
  if (!response.ok) throw new Error("node-tags-save-failed");
}

/**
 * Why a node revocation did not take effect, in terms the operator can act on:
 * `session` (expired second factor or rejected CSRF), `active-guest` (an enrolled guest must be
 * revoked first, or the node was already revoked), or `service`.
 */
export type NodeRevokeFailure = "session" | "active-guest" | "service";

/** A revocation the Worker refused; the reason is safe to show and carries no response body. */
export class NodeRevokeError extends Error {
  /** Create an operator-safe revocation error. */
  constructor(readonly reason: NodeRevokeFailure) {
    super(reason);
  }
}

/**
 * Permanently revoke one node identity. Its key can no longer upload; the node must re-enroll
 * to report again. The Worker also cancels the node's maintenance windows and disables its rules.
 *
 * @throws NodeRevokeError when the Worker refuses the request.
 */
export async function revokeNode(nodeId: string, csrf: string): Promise<void> {
  const response = await fetch(`/api/admin/nodes/${encodeURIComponent(nodeId)}`, {
    method: "DELETE",
    credentials: "same-origin",
    headers: { "X-CSRF-Token": csrf },
  });
  if (response.ok) return;
  if (response.status === 401 || response.status === 403) throw new NodeRevokeError("session");
  if (response.status === 409) throw new NodeRevokeError("active-guest");
  throw new NodeRevokeError("service");
}

/** Fetch and validate projects and their current node assignments. */
export async function fetchProjects(): Promise<readonly Project[]> {
  const response = await fetch("/api/admin/projects", {
    credentials: "same-origin",
  });
  if (!response.ok) throw new Error("projects-unavailable");
  return z.object({ projects: z.array(projectSchema) }).parse(
    await response.json(),
  ).projects;
}

/** Create or update one project through the CSRF-bound administrator API. */
export async function saveProject(
  project: ProjectDraft,
  csrf: string,
  projectId?: string,
): Promise<void> {
  const response = await fetch(
    projectId
      ? `/api/admin/projects/${encodeURIComponent(projectId)}`
      : "/api/admin/projects",
    {
      method: projectId ? "PUT" : "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrf },
      body: JSON.stringify(project),
    },
  );
  if (!response.ok) throw new Error("project-save-failed");
}

/** Permanently delete one project while leaving every assigned node intact. */
export async function deleteProject(
  projectId: string,
  csrf: string,
): Promise<void> {
  const response = await fetch(
    `/api/admin/projects/${encodeURIComponent(projectId)}`,
    {
      method: "DELETE",
      credentials: "same-origin",
      headers: { "X-CSRF-Token": csrf },
    },
  );
  if (!response.ok) throw new Error("project-delete-failed");
}

const scopeFromRow = (type: string, id: string | null) =>
  type === "node"
    ? { type: "node" as const, nodeId: id ?? "" }
    : type === "project"
    ? { type: "project" as const, projectId: id ?? "" }
    : { type: "fleet" as const };
const fleetColumnSchema = z.enum(
  [
    "status",
    "name",
    "project",
    "location",
    "release",
    "disk",
    "memory",
    "backup",
    "update",
    "last-seen",
  ] satisfies readonly FleetColumn[],
);

function maintenanceScopeFromRow(
  type: string,
  id: string | null,
  windowId: string,
  targets: readonly Record<string, unknown>[],
): MaintenanceScope {
  const nodeIds = targets.filter((target) =>
    String(target.window_id) === windowId
  ).map((target) => z.string().uuid().parse(target.node_id));
  if (type === "node" && nodeIds.length > 1) return { type: "nodes", nodeIds };
  return scopeFromRow(type, id);
}

/** Fetch saved views, maintenance, alerting, incidents, compliance, and backup posture. */
export async function fetchOperations(): Promise<OperationsState> {
  const response = await fetch("/api/admin/operations", {
    credentials: "same-origin",
  });
  if (!response.ok) throw new Error("operations-unavailable");
  const row = z.record(z.string(), z.unknown());
  const body = z.object({
    views: z.array(row),
    maintenance: z.array(row),
    maintenanceTargets: z.array(row),
    rules: z.array(row),
    incidents: z.array(row),
    incidentEvents: z.array(row),
    policies: z.array(row),
    servicePolicies: z.array(row),
    reportJobs: z.array(row),
    backups: z.array(row),
    software: z.array(row),
    serviceInventory: z.array(row),
    csrf: z.string(),
  }).parse(await response.json());
  return {
    views: body.views.map((item) => ({
      id: String(item.id),
      name: String(item.name),
      filter: fleetFilterSchema.parse(JSON.parse(String(item.filter_json))),
      sort: z.enum(["name", "status", "last-seen"]).parse(item.sort_key),
      columns: z.array(fleetColumnSchema).min(1).max(10).parse(
        JSON.parse(String(item.columns_json)),
      ),
      groupBy: z.enum(["none", "project", "status", "location"]).parse(
        item.group_by,
      ),
      isDefault: Number(item.is_default) === 1,
    })),
    maintenance: body.maintenance.map((item) => ({
      id: String(item.id),
      scope: maintenanceScopeFromRow(
        String(item.scope_type),
        item.scope_id === null ? null : String(item.scope_id),
        String(item.id),
        body.maintenanceTargets,
      ),
      startsAt: String(item.starts_at),
      endsAt: String(item.ends_at),
      timezone: String(item.timezone),
      reason: String(item.reason),
      state: z.enum(["scheduled", "cancelled"]).parse(item.state),
    })),
    rules: body.rules.map((item) => {
      const metric = z.enum([
        "contact-age",
        "cpu",
        "memory",
        "disk",
        "container-health",
        "restart-count",
        "backup-age",
        "version-compliance",
      ]).parse(item.metric);
      const comparison = z.enum(["gt", "gte", "lt", "lte", "eq"]).parse(
        item.comparison,
      );
      return {
        ...createAlertCondition(
          metric,
          comparison,
          Number(item.threshold),
          Number(item.hysteresis),
        ),
        id: String(item.id),
        name: String(item.name),
        durationMinutes: Number(item.duration_minutes),
        cooldownMinutes: Number(item.cooldown_minutes),
        severity: z.enum(["warning", "critical"]).parse(item.severity),
        scope: scopeFromRow(
          String(item.scope_type),
          item.scope_id === null ? null : String(item.scope_id),
        ),
        notificationRoute: item.notification_route_type === "none"
          ? { type: "none" as const }
          : { type: "ntfy" as const, secretRef: "NTFY_URL" as const },
        enabled: Number(item.enabled) === 1,
      };
    }),
    incidents: body.incidents.map((item) => ({
      id: String(item.id),
      title: String(item.title),
      severity: String(item.severity),
      state: String(item.state),
      openedAt: String(item.opened_at),
    })),
    incidentEvents: body.incidentEvents.map((item) => ({
      id: String(item.id),
      incidentId: String(item.incident_id),
      occurredAt: String(item.occurred_at),
      actor: String(item.actor),
      kind: String(item.kind),
      message: String(item.message),
      nodeId: typeof item.node_id === "string" ? item.node_id : null,
      serviceId: typeof item.service_id === "string" ? item.service_id : null,
    })),
    policies: body.policies.map((item) => ({
      component: String(item.component),
      desiredVersion: String(item.desired_version),
      minimumSupportedVersion: item.minimum_supported_version === null
        ? null
        : String(item.minimum_supported_version),
      immutableRequired: Number(item.immutable_required) === 1,
    })),
    servicePolicies: body.servicePolicies.map((item) => ({
      catalogId: String(item.catalog_id),
      desiredImage: String(item.desired_image),
      desiredDigest: String(item.desired_digest),
    })),
    reportJobs: body.reportJobs.map((item) => {
      const scope = z.intersection(
        z.discriminatedUnion("type", [
          z.object({ type: z.literal("fleet") }),
          z.object({
            type: z.literal("project"),
            projectId: z.string().uuid(),
          }),
        ]),
        z.object({ window: z.enum(["24h", "7d", "30d"]) }),
      ).parse(JSON.parse(String(item.scope_json)));
      return {
        id: z.string().uuid().parse(item.id),
        format: z.enum(["pdf", "csv"]).parse(item.format),
        scope: scope.type === "project"
          ? { type: "project" as const, projectId: scope.projectId }
          : { type: "fleet" as const },
        window: scope.window,
        state: z.enum(["queued", "ready", "failed", "expired"]).parse(
          item.state,
        ),
        requestedAt: z.string().datetime().parse(item.requested_at),
        expiresAt: z.string().datetime().parse(item.expires_at),
        errorCode: item.error_code === null ? null : String(item.error_code),
        sizeBytes: item.size_bytes === null ? null : Number(item.size_bytes),
        completedAt: item.completed_at === null
          ? null
          : String(item.completed_at),
      };
    }),
    backups: body.backups.map((item) => ({
      id: String(item.id),
      target: String(item.target),
      capturedAt: String(item.captured_at),
      state: z.enum(["succeeded", "failed"]).parse(item.state),
      restoreTestedAt: item.restore_tested_at === null
        ? null
        : String(item.restore_tested_at),
      restoreSchemaVerified: Number(item.restore_schema_verified) === 1,
      restoreDecryptionVerified: Number(item.restore_decryption_verified) === 1,
      keyEscrowVerified: Number(item.key_escrow_verified) === 1,
      errorCode: item.error_code === null ? null : String(item.error_code),
    })),
    software: body.software.map((item) => ({
      nodeId: String(item.node_id),
      nodeName: String(item.display_name),
      projectIds: z.array(z.string().uuid()).parse(
        JSON.parse(String(item.project_ids_json)),
      ),
      projectNames: z.array(z.string()).parse(
        JSON.parse(String(item.project_names_json)),
      ),
      target: String(item.target),
      agent: item.agent_version === null ? null : String(item.agent_version),
      appliance: item.appliance_version === null
        ? null
        : String(item.appliance_version),
      os: item.os_version === null ? null : String(item.os_version),
      docker: item.docker_version === null ? null : String(item.docker_version),
      compose: item.compose_version === null
        ? null
        : String(item.compose_version),
      immutable: Number(item.immutable) === 1,
      observedAt: String(item.observed_at),
    })),
    serviceInventory: body.serviceInventory.map((item) => ({
      nodeId: String(item.node_id),
      nodeName: String(item.display_name),
      catalogId: String(item.catalog_id),
      image: String(item.image),
      imageDigest: item.image_digest === null
        ? null
        : String(item.image_digest),
      state: String(item.state),
      health: String(item.health),
      observedAt: String(item.observed_at),
    })),
  };
}

const fleetFilterSchema = z.object({
  query: z.string(),
  statuses: z.array(
    z.enum(["healthy", "warning", "critical", "stale", "offline"]),
  ),
  tags: z.array(z.string()),
  projectIds: z.array(z.string().uuid()).default([]),
  locations: z.array(z.string()).default([]),
  releases: z.array(z.string()).default([]),
  capabilities: z.array(z.string()).default([]),
  maxContactAgeMinutes: z.number().int().nonnegative().nullable().default(null),
});

async function mutateOperations(
  path: string,
  value: unknown,
  csrf: string,
  method: "POST" | "PUT" = "POST",
): Promise<void> {
  const response = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", "X-CSRF-Token": csrf },
    body: JSON.stringify(value),
  });
  if (!response.ok) throw new Error("operation-save-failed");
}

async function deleteOperation(path: string, csrf: string): Promise<void> {
  const response = await fetch(path, {
    method: "DELETE",
    credentials: "same-origin",
    headers: { "X-CSRF-Token": csrf },
  });
  if (!response.ok) throw new Error("operation-delete-failed");
}

/** Save one closed-grammar fleet view. */
export async function createFleetView(
  value: Omit<FleetView, "id">,
  csrf: string,
): Promise<void> {
  await mutateOperations("/api/admin/views", value, csrf);
}

/** Update one saved fleet view. */
export async function updateFleetView(
  id: string,
  value: Omit<FleetView, "id">,
  csrf: string,
): Promise<void> {
  await mutateOperations(
    `/api/admin/views/${encodeURIComponent(id)}`,
    value,
    csrf,
    "PUT",
  );
}

/** Delete one saved fleet view. */
export async function deleteFleetView(id: string, csrf: string): Promise<void> {
  await deleteOperation(`/api/admin/views/${encodeURIComponent(id)}`, csrf);
}

/** Schedule a maintenance window. */
export async function createMaintenance(
  value: Omit<MaintenanceWindow, "id" | "state">,
  csrf: string,
): Promise<void> {
  await mutateOperations("/api/admin/maintenance", value, csrf);
}

/** Update a scheduled maintenance window. */
export async function updateMaintenance(
  id: string,
  value: Omit<MaintenanceWindow, "id" | "state">,
  csrf: string,
): Promise<void> {
  await mutateOperations(
    `/api/admin/maintenance/${encodeURIComponent(id)}`,
    value,
    csrf,
    "PUT",
  );
}

/** Cancel a maintenance window without deleting audit evidence. */
export async function cancelMaintenance(
  id: string,
  csrf: string,
): Promise<void> {
  await deleteOperation(
    `/api/admin/maintenance/${encodeURIComponent(id)}`,
    csrf,
  );
}

/**
 * Build the exact alert rule body the Worker accepts.
 *
 * The Worker parses alert rule requests with a strict schema, so a stored rule spread into a
 * request (which still carries its `id`) would be refused with 400 and the edit silently lost.
 * Listing the fields here keeps every create, update and preview call inside that contract; the
 * unit is re-derived from the metric, which is the only unit the Worker accepts for it.
 */
function alertRuleBody(value: Omit<AlertRule, "id">): Omit<AlertRule, "id"> {
  return {
    ...createAlertCondition(
      value.metric,
      value.comparison,
      value.threshold,
      value.hysteresis,
    ),
    name: value.name,
    durationMinutes: value.durationMinutes,
    severity: value.severity,
    scope: value.scope,
    enabled: value.enabled,
    notificationRoute: value.notificationRoute,
    cooldownMinutes: value.cooldownMinutes,
  };
}

/** Create one notification-only alert rule. */
export async function createAlertRule(
  value: Omit<AlertRule, "id">,
  csrf: string,
): Promise<void> {
  await mutateOperations("/api/admin/alert-rules", alertRuleBody(value), csrf);
}

/** Preview an alert rule against bounded retained history without saving it. */
export async function previewAlertRule(
  value: Omit<AlertRule, "id">,
  csrf: string,
): Promise<
  {
    readonly sampleCount: number;
    readonly matchedSamples: number;
    readonly matchedNodeCount: number;
    readonly firingEpisodes: number;
    readonly recoveries: number;
    readonly suppressedSamples: number;
    readonly lookback: string;
  }
> {
  const response = await fetch("/api/admin/alert-rules/preview", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", "X-CSRF-Token": csrf },
    body: JSON.stringify(alertRuleBody(value)),
  });
  if (!response.ok) throw new Error("alert-preview-failed");
  return z.object({
    sampleCount: z.number().int(),
    matchedSamples: z.number().int(),
    matchedNodeCount: z.number().int(),
    firingEpisodes: z.number().int(),
    recoveries: z.number().int(),
    suppressedSamples: z.number().int(),
    lookback: z.string(),
  }).parse(await response.json());
}

/** Update one alert rule. */
export async function updateAlertRule(
  id: string,
  value: Omit<AlertRule, "id">,
  csrf: string,
): Promise<void> {
  await mutateOperations(
    `/api/admin/alert-rules/${encodeURIComponent(id)}`,
    alertRuleBody(value),
    csrf,
    "PUT",
  );
}

/** Delete one alert rule and its derived state. */
export async function deleteAlertRule(id: string, csrf: string): Promise<void> {
  await deleteOperation(
    `/api/admin/alert-rules/${encodeURIComponent(id)}`,
    csrf,
  );
}

/** Store an operator-approved version policy without initiating any update. */
export async function saveVersionPolicy(
  value: {
    readonly component: "agent" | "appliance" | "os" | "docker" | "compose";
    readonly desiredVersion: string;
    readonly minimumSupportedVersion: string;
    readonly immutableRequired: boolean;
  },
  csrf: string,
): Promise<void> {
  await mutateOperations("/api/admin/version-policies", value, csrf);
}

/** Store an immutable desired image policy without exposing an update action. */
export async function saveServiceVersionPolicy(
  value: {
    readonly catalogId: string;
    readonly desiredImage: string;
    readonly desiredDigest: string;
  },
  csrf: string,
): Promise<void> {
  await mutateOperations("/api/admin/service-version-policies", value, csrf);
}

/** Queue one private, expiring fleet or project report artifact. */
export async function createReportJob(
  value: {
    readonly window: "24h" | "7d" | "30d";
    readonly format: "pdf" | "csv";
    readonly scope: { readonly type: "fleet" } | {
      readonly type: "project";
      readonly projectId: string;
    };
  },
  csrf: string,
): Promise<void> {
  await mutateOperations("/api/admin/report-jobs", value, csrf);
}

/** Acknowledge, resolve, or annotate an incident without touching a node. */
export async function updateIncident(
  id: string,
  action: "acknowledge" | "resolve" | "note",
  csrf: string,
  note?: string,
): Promise<void> {
  await mutateOperations(`/api/admin/incidents/${encodeURIComponent(id)}`, {
    action,
    ...(note ? { note } : {}),
  }, csrf);
}

/** Merge one incident into another while retaining immutable source events. */
export async function mergeIncident(
  id: string,
  targetIncidentId: string,
  csrf: string,
): Promise<void> {
  await mutateOperations(`/api/admin/incidents/${encodeURIComponent(id)}`, {
    action: "merge",
    targetIncidentId,
  }, csrf);
}

/** Split selected immutable source events into a new incident. */
export async function splitIncident(
  id: string,
  eventIds: readonly string[],
  title: string,
  csrf: string,
): Promise<void> {
  await mutateOperations(`/api/admin/incidents/${encodeURIComponent(id)}`, {
    action: "split",
    eventIds,
    title,
  }, csrf);
}

/** Fetch bounded project availability evidence. */
export async function fetchProjectHealth(
  id: string,
  window: "24h" | "7d" | "30d",
): Promise<
  { readonly samples: number; readonly availabilityPercent: number | null }
> {
  const response = await fetch(
    `/api/admin/projects/${encodeURIComponent(id)}/health?window=${window}`,
    { credentials: "same-origin" },
  );
  if (!response.ok) throw new Error("project-health-unavailable");
  return z.object({
    samples: z.number(),
    availabilityPercent: z.number().nullable(),
  }).parse(await response.json());
}

const notificationStatusSchema = z.object({
  route: z.discriminatedUnion("status", [
    z.object({ status: z.literal("configured"), source: z.enum(["split", "legacy"]), identity: z.string().max(200), migrationRequired: z.boolean() }),
    z.object({ status: z.literal("invalid"), problem: z.string().max(64), migrationRequired: z.boolean() }),
    z.object({ status: z.literal("not-configured"), migrationRequired: z.boolean() }),
  ]),
  lastDelivery: z.object({ state: z.string().max(32), errorCode: z.string().max(64).nullable(), at: z.string() }).nullable(),
  tests: z.array(z.object({
    id: z.string().uuid(),
    requestedAt: z.string(),
    outcome: z.string().max(64),
    receiptId: z.string().max(64).nullable(),
    identity: z.string().max(200),
  })).max(5),
  subscriptions: z.array(z.object({
    id: z.string().uuid(),
    clientLabel: z.string().max(64),
    verifiedAt: z.string(),
    current: z.boolean(),
  })).max(20),
});

/** Redacted ntfy route state, recent tests and operator-confirmed subscriptions. */
export type NotificationStatus = z.infer<typeof notificationStatusSchema>;

/** Outcome of one test publish; the provider response itself is never exposed. */
export type NotificationTestResult =
  | { readonly status: "sent"; readonly id: string; readonly outcome: string; readonly receiptId: string | null }
  | { readonly status: "rate-limited" }
  | { readonly status: "failed"; readonly id: string | null; readonly outcome: string };

/** Fetch the redacted ntfy notification status. */
export async function fetchNotificationStatus(): Promise<NotificationStatus> {
  const response = await fetch("/api/admin/notifications", { credentials: "same-origin" });
  if (!response.ok) throw new Error("notifications-unavailable");
  return notificationStatusSchema.parse(await response.json());
}

/** Ask the Worker to publish one test notification (one per minute, audited). */
export async function sendTestNotification(csrf: string): Promise<NotificationTestResult> {
  const response = await fetch("/api/admin/notifications/test", {
    method: "POST",
    credentials: "same-origin",
    headers: { "X-CSRF-Token": csrf },
  });
  if (response.status === 429) return { status: "rate-limited" };
  const body = z.object({ id: z.string().uuid().optional(), outcome: z.string().max(64).optional(), receiptId: z.string().max(64).nullable().optional() })
    .parse(await response.json());
  if (response.ok && body.id !== undefined) {
    return { status: "sent", id: body.id, outcome: body.outcome ?? "delivered", receiptId: body.receiptId ?? null };
  }
  return { status: "failed", id: body.id ?? null, outcome: body.outcome ?? "request-failed" };
}

/** Record that a named device received a delivered test notification. */
export async function confirmNotificationReceipt(testId: string, clientLabel: string, csrf: string): Promise<void> {
  const response = await fetch("/api/admin/notifications/subscriptions", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", "X-CSRF-Token": csrf },
    body: JSON.stringify({ testId, clientLabel }),
  });
  if (!response.ok) throw new Error("receipt-not-recorded");
}

/** Forget a confirmed device, for example after token rotation or loss. */
export async function removeNotificationSubscription(id: string, csrf: string): Promise<void> {
  const response = await fetch(`/api/admin/notifications/subscriptions/${encodeURIComponent(id)}`, {
    method: "DELETE",
    credentials: "same-origin",
    headers: { "X-CSRF-Token": csrf },
  });
  if (!response.ok) throw new Error("subscription-not-removed");
}
